// Notification list body shared by the bell dropdown and the /notifications page (Phase 46, 46-20).
// Renders loading, error, empty, flat and date-grouped states (UI-SPEC contracts 2 and 3). All
// strings come from NOTIFICATION_CHROME in the shared copy file.

import { AlertCircle, Bell } from "lucide-react";
import { NOTIFICATION_CHROME } from "@shared/notificationCopy";
import { Link } from "@/components/Link";
import { Skeleton } from "@/components/ui/skeleton";
import type { ClientNotification } from "@/api/notificationService";
import { groupNotificationsByDate } from "@/lib/notificationUi";
import NotificationRow, { type NotificationListSurface } from "./NotificationRow";

type Props = {
  surface: NotificationListSurface;
  items: ClientNotification[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  limitSkeletons: 3 | 5;
  grouped?: boolean;
  onNavigate?: () => void;
};

const SkeletonRow = () => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, padding: 16, borderBottom: "1px solid var(--ys-rule)" }}>
    <Skeleton style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
      <Skeleton style={{ width: "60%", height: 12 }} />
      <Skeleton style={{ width: "30%", height: 12 }} />
    </div>
  </div>
);

export default function NotificationList({
  surface,
  items,
  loading,
  error,
  onRetry,
  limitSkeletons,
  grouped = false,
  onNavigate,
}: Props) {
  const compact = surface === "bell_dropdown";
  const base = { fontFamily: "var(--ys-font)" } as const;

  if (loading && items.length === 0) {
    return (
      <div dir="rtl" style={base} aria-busy="true">
        {Array.from({ length: limitSkeletons }, (_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div
        dir="rtl"
        role="alert"
        style={{
          ...base,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: compact ? 8 : 16,
          padding: compact ? "24px 16px" : "48px 16px",
          textAlign: "center",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ys-danger)" }}>
          <AlertCircle size={16} aria-hidden="true" />
          {NOTIFICATION_CHROME.error}
        </span>
        <button
          type="button"
          onClick={onRetry}
          style={{
            ...base,
            fontSize: 14,
            fontWeight: 600,
            padding: "8px 16px",
            border: "1px solid var(--ys-rule-2)",
            borderRadius: "var(--ys-r)",
            background: "var(--ys-paper)",
            color: "var(--ys-ink)",
            cursor: "pointer",
          }}
        >
          {NOTIFICATION_CHROME.retry}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        dir="rtl"
        style={{
          ...base,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "48px 16px",
          textAlign: "center",
        }}
      >
        <Bell size={32} color="var(--ys-ink-3)" aria-hidden="true" />
        <p style={{ margin: "8px 0 0", fontSize: 16, fontWeight: 600, lineHeight: 1.4, color: "var(--ys-ink)" }}>
          {NOTIFICATION_CHROME.emptyHeading}
        </p>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--ys-ink-2)" }}>{NOTIFICATION_CHROME.emptyBody}</p>
        <Link
          to="/stores"
          onClick={onNavigate}
          style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: "var(--ys-primary)", textDecoration: "none" }}
        >
          {NOTIFICATION_CHROME.emptyCta}
        </Link>
      </div>
    );
  }

  const renderRow = (n: ClientNotification) => (
    <NotificationRow key={n.id} notification={n} surface={surface} onNavigate={onNavigate} />
  );

  if (!grouped) {
    return (
      <div dir="rtl" style={base}>
        {items.map(renderRow)}
      </div>
    );
  }

  return (
    <div dir="rtl" style={base}>
      {groupNotificationsByDate(items).map((group) => (
        <section key={group.key} aria-label={group.label}>
          <h2
            style={{
              margin: 0,
              padding: "16px 16px 8px",
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.4,
              color: "var(--ys-ink)",
              borderBottom: "1px solid var(--ys-rule)",
            }}
          >
            {group.label}
          </h2>
          {group.items.map(renderRow)}
        </section>
      ))}
    </div>
  );
}
